import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Field } from 'formik';
import get from 'lodash/get';
import { set } from 'lodash';
import FormikAutocompleteSelect from '../../Fields/FormikAutocompleteSelect';
import { TP } from '../../../constants/consts';
import NoOptionsBox from '../../NoOptionsBox';
import Button from '../../Button';
import { useTranslation } from '../../../src/i18n';
import AddNewComposerModal from '../../AddNewComposerModal';
import AddNewWorkModal from '../../AddNewWorkModal';
import { getOption } from '../../../utils/common';

const emptyModalState = { name: null, fieldName: '' };

const ComposerWorkFields = ({
  composerField,
  workField,
  searchWorkFilters,
  searchComposersFilters,
  composers,
  works,
  setValues,
  values,
  resetComposerOptions,
  loadingValue,
  disabled,
  resetMusicWorksOptions,
  onWorkChange,
  composerGridParams,
  workGridParams,
  setFieldValue,
  preventWorkSetValues,
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = React.useState('');
  const [activeModal, setActiveModal] = React.useState(emptyModalState);
  const [workCreatableValue, setWorkCreatableValue] = React.useState('');

  const noOptionMessage = (buttonTitle, openFunction) => ({
    NoOptionsMessage: () => <NoOptionsBox buttonText={`${TP}.${buttonTitle}`} openFormFunc={openFunction} t={t} />,
  });

  const updateValues = (fieldName, value, nullableFieldName) => {
    const valuesToUpdate = { ...values };
    set(valuesToUpdate, fieldName, value);
    if (nullableFieldName) {
      set(valuesToUpdate, nullableFieldName, null);
    }
    return valuesToUpdate;
  };

  return (
    <>
      <Grid item {...composerGridParams}>
        <Field
          name={composerField.name}
          instanceId={composerField.name}
          label={t(composerField.label)}
          options={composers}
          onInputTextChange={v => {
            searchComposersFilters(composerField.name, v);
            setTimeout(() => setQuery(v), 500);
          }}
          onChange={v => {
            setValues(updateValues(composerField.name, v, workField.name));
            resetComposerOptions(composerField.name);
            setQuery('');
          }}
          onBlur={() => {
            setQuery('');
          }}
          isLoading={loadingValue === composerField.name}
          component={FormikAutocompleteSelect}
          isDisabled={disabled}
          customComponents={{
            ...noOptionMessage('FN_ADD_COMPOSER_TO_LIST', () =>
              setActiveModal({ name: 'composer', fieldName: composerField.name }),
            ),
          }}
          staticOption={
            <Button onClick={() => setActiveModal({ name: 'composer', fieldName: composerField.name })}>
              {t(`${TP}.FN_ADD_COMPOSER_TO_LIST`)}
            </Button>
          }
          menuIsOpen={!!query.length}
          isClearable
        />
      </Grid>
      <Grid item {...workGridParams}>
        <Field
          name={workField.name}
          instanceId={workField.name}
          label={t(workField.label)}
          options={works}
          onInputTextChange={v => {
            setWorkCreatableValue(v);
            searchWorkFilters(workField.name, v, get(values, `${composerField.name}.value`));
          }}
          onFocus={() => {
            searchWorkFilters(workField.name, '', get(values, `${composerField.name}.value`));
          }}
          onChange={v => {
            resetMusicWorksOptions(workField.name);
            if (!preventWorkSetValues) {
              setValues(updateValues(workField.name, v));
            }
            onWorkChange(v);
          }}
          isLoading={loadingValue === workField.name}
          component={FormikAutocompleteSelect}
          preventDefaultOnChange
          isDisabled={disabled || !get(values, composerField.name)}
          customComponents={{
            ...noOptionMessage('FN_ADD_WORK_BUTTON', () =>
              setActiveModal({ name: 'work', fieldName: workField.name, composer: get(values, composerField.name) }),
            ),
          }}
          staticOption={
            <Button
              onClick={() =>
                setActiveModal({ name: 'work', fieldName: workField.name, composer: get(values, composerField.name) })
              }
            >
              {t(`${TP}.FN_ADD_WORK_BUTTON`)}
            </Button>
          }
          isClearable
        />
      </Grid>
      {activeModal.name === 'composer' && (
        <AddNewComposerModal
          onClose={v => {
            if (v?.name) {
              setFieldValue(activeModal.fieldName, { value: v.id, label: v.name });
            }
            setActiveModal(emptyModalState);
          }}
        />
      )}
      {activeModal.name === 'work' && (
        <AddNewWorkModal
          onClose={v => {
            const workOption = v.name ? getOption(v) : undefined;
            if (workOption) {
              setFieldValue(activeModal.fieldName, workOption);
            }
            setActiveModal(emptyModalState);
            // main work field
            if (activeModal.fieldName === workField.name) {
              onWorkChange(workOption);
            }
          }}
          composerValue={activeModal?.composer}
          workValue={workCreatableValue}
        />
      )}
    </>
  );
};

ComposerWorkFields.propTypes = {
  composerField: PropTypes.object.isRequired,
  workField: PropTypes.object.isRequired,
  nameField: PropTypes.object,
  composers: PropTypes.array.isRequired,
  works: PropTypes.array.isRequired,
  searchWorkFilters: PropTypes.func.isRequired,
  searchComposersFilters: PropTypes.func.isRequired,
  resetComposerOptions: PropTypes.func.isRequired,
  loadingValue: PropTypes.string,
  setValues: PropTypes.func.isRequired,
  resetMusicWorksOptions: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onWorkChange: PropTypes.func,
  composerGridParams: PropTypes.object,
  workGridParams: PropTypes.object,
  preventWorkSetValues: PropTypes.bool,
};

ComposerWorkFields.defaultProps = {
  disabled: false,
  onWorkChange: () => {},
  nameField: null,
  composerGridParams: { md: 4, sm: 6, xs: 12 },
  workGridParams: { md: 6, sm: 6, xs: 12 },
  preventWorkSetValues: false,
};

export default memo(ComposerWorkFields);
